import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { turkishIncludes } from '../lib/turkishSearch';
import { cn } from '../lib/utils';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../components/ui/hover-card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Eye,
  Search,
  Folder,
  FileImage,
  FileSpreadsheet,
  File,
  Calendar,
  User,
  Building2,
  Loader2,
  Upload,
  Plus,
  Trash2,
  MoreHorizontal,
  Edit,
  Users,
  X,
  CheckCircle,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Shield,
  ShieldCheck
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// PDF.js worker - use unpkg CDN with dynamic version matching
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// File type icons
const getFileIcon = (fileType) => {
  const type = fileType?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(type)) {
    return <FileImage className="h-5 w-5 text-purple-500" />;
  }
  if (['xls', 'xlsx'].includes(type)) {
    return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
  }
  if (['doc', 'docx'].includes(type)) {
    return <FileText className="h-5 w-5 text-blue-600" />;
  }
  if (type === 'pdf') {
    return <FileText className="h-5 w-5 text-red-500" />;
  }
  return <File className="h-5 w-5 text-slate-400" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const DocumentManagement = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [selectedFirmFilter, setSelectedFirmFilter] = useState('all');
  
  // Upload dialog state
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('COMPANY');
  const [assignToAll, setAssignToAll] = useState(true);
  const [accessLevel, setAccessLevel] = useState('all'); // all, super_admins, firm_admins, custom, myself
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedFirmForUpload, setSelectedFirmForUpload] = useState('all'); // For Super Admin
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState({ open: false, document: null });
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('COMPANY');
  
  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null });
  
  // Preview dialog state
  const [previewDialog, setPreviewDialog] = useState({ open: false, document: null, blobUrl: null });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfScale, setPdfScale] = useState(1.0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isAnyAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN';
  const canFilterFirm = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN';

  // Check if file is previewable as image
  const isImageFile = (fileType) => {
    const type = fileType?.toLowerCase();
    const shortTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
    const mimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    return shortTypes.includes(type) || mimeTypes.includes(type);
  };

  // Check if file is PDF
  const isPdfFile = (fileType) => {
    const type = fileType?.toLowerCase();
    return type === 'pdf' || type === 'application/pdf';
  };

  // Check if file is previewable
  const isPreviewable = (fileType) => {
    return isImageFile(fileType) || isPdfFile(fileType);
  };

  // Handle preview - fetch file as blob and display
  const handlePreview = async (doc) => {
    if (!isPreviewable(doc.file_type)) {
      toast.info(t('documents.previewNotSupported') || 'Bu dosya türü önizlenemiyor');
      handleDownload(doc);
      return;
    }

    setPreviewLoading(true);
    setPreviewDialog({ open: true, document: doc, blobUrl: null });
    setPdfPageNumber(1);
    setPdfNumPages(null);
    setPdfScale(1.0);

    try {
      // Şub 2026 — Hızlı yol: S3'te ise presigned URL al, tarayıcı direkt S3'ten çeker
      // (backend'den blob stream'lemek yerine). Diğer backend'lerde eski blob yoluna düşer.
      let directUrl = null;
      try {
        const presign = await api.get(`/documents/${doc.id}/download-url?preview=true`);
        if (presign.data?.success && presign.data?.data?.url) {
          directUrl = presign.data.data.url;
        }
      } catch { /* presign yoksa blob yoluyla devam */ }

      if (directUrl) {
        setPreviewDialog({ open: true, document: doc, blobUrl: directUrl, isDirectUrl: true });
      } else {
        const response = await api.get(`/documents/${doc.id}/download?preview=true`, {
          responseType: 'blob'
        });

        // Get proper MIME type - handle both short types and full MIME types
        const mimeTypes = {
          'pdf': 'application/pdf',
          'application/pdf': 'application/pdf',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'image/jpeg': 'image/jpeg',
          'png': 'image/png',
          'image/png': 'image/png',
          'gif': 'image/gif',
          'image/gif': 'image/gif',
          'webp': 'image/webp',
          'image/webp': 'image/webp',
          'bmp': 'image/bmp',
          'image/bmp': 'image/bmp'
        };
        const mimeType = mimeTypes[doc.file_type?.toLowerCase()] || 'application/octet-stream';

        const blob = new Blob([response.data], { type: mimeType });
        const blobUrl = window.URL.createObjectURL(blob);

        setPreviewDialog({ open: true, document: doc, blobUrl, isDirectUrl: false });
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(t('documents.previewError') || 'Dosya önizlenemedi');
      setPreviewDialog({ open: false, document: null, blobUrl: null });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Cleanup blob URL when dialog closes
  const handleClosePreview = () => {
    if (previewDialog.blobUrl && !previewDialog.isDirectUrl) {
      window.URL.revokeObjectURL(previewDialog.blobUrl);
    }
    setPreviewDialog({ open: false, document: null, blobUrl: null });
    setPdfNumPages(null);
    setPdfPageNumber(1);
  };

  // PDF document loaded callback
  const onPdfLoadSuccess = ({ numPages }) => {
    setPdfNumPages(numPages);
  };

  useEffect(() => {
    fetchDocuments();
    fetchUsers();
    if (canFilterFirm) {
      fetchFirms();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/documents');
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error(t('documents.fetchError') || 'Dosyalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchFirms = async () => {
    try {
      const response = await api.get('/firms/');
      setFirms(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Failed to fetch firms:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users?status=ACTIVE');
      const usersList = response.data?.data || response.data || [];
      // Show all users for selection (employees, firm admins, full admins, super admins)
      setUsers(usersList);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // For custom user selection: show ALL active users (including super admins)
  const usersForUpload = users;

  // Resolve assignee ids (doc.assigned_to array or legacy doc.user_id) into
  // {id, name} pairs using the fetched users list. Unknown ids fall through
  // as a short id fragment so the hovercard is still informative + clickable.
  const getAssignees = (doc) => {
    const ids = doc.assigned_to?.length
      ? doc.assigned_to
      : doc.user_id
      ? [doc.user_id]
      : [];
    if (ids.length === 0) return [];
    return ids.map((uid) => {
      const u = users.find((x) => x.id === uid);
      return {
        id: uid,
        name: u?.full_name || u?.email || `#${String(uid).slice(0, 8)}`,
      };
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xlsx', 'xls'].includes(ext);
    });
    
    if (validFiles.length !== files.length) {
      toast.warning(t('documents.invalidFileType') || 'Bazı dosyalar desteklenmeyen formatta');
    }
    
    // Add custom name field
    const filesWithNames = validFiles.map(file => ({
      file,
      customName: '',
      originalName: file.name
    }));
    
    setUploadFiles(filesWithNames);
    if (validFiles.length > 0) {
      setUploadDialog(true);
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      
      // Add files
      uploadFiles.forEach(({ file }) => {
        formData.append('files', file);
      });
      
      // Add custom names (comma-separated)
      const fileNames = uploadFiles.map(f => f.customName || '').join(',');
      formData.append('file_names', fileNames);
      
      // Add category
      formData.append('category', uploadCategory);
      
      // Add target firm for Super Admin
      if (isSuperAdmin) {
        formData.append('target_firm_id', selectedFirmForUpload);
      }
      
      // Add access level
      formData.append('access_level', accessLevel === 'myself' ? 'custom' : accessLevel);
      
      // Add assigned users
      if (accessLevel === 'myself') {
        formData.append('assigned_to', user.id);
      } else if (accessLevel === 'custom') {
        formData.append('assigned_to', selectedUsers.join(','));
      } else {
        formData.append('assigned_to', 'all');
      }
      
      const response = await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data?.message || 'Dosyalar yüklendi');
      setUploadDialog(false);
      setUploadFiles([]);
      setSelectedUsers([]);
      setAssignToAll(true);
      setAccessLevel('all');
      setSelectedFirmForUpload('all');
      fetchDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('documents.uploadError') || 'Dosya yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      // Şub 2026 — S3 dosyaları için presigned URL ile direkt indirme.
      try {
        const presign = await api.get(`/documents/${doc.id}/download-url?preview=false`);
        if (presign.data?.success && presign.data?.data?.url) {
          const link = document.createElement('a');
          link.href = presign.data.data.url;
          link.setAttribute('download', doc.original_name || `${doc.file_name}.${doc.file_type}`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          return;
        }
      } catch { /* presign yoksa blob'a düş */ }

      const response = await api.get(`/documents/${doc.id}/download?preview=true`, {
        responseType: 'blob'
      });
      
      // Get proper MIME type
      const mimeTypes = {
        'pdf': 'application/pdf',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'webp': 'image/webp',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      };
      const mimeType = mimeTypes[doc.file_type?.toLowerCase()] || 'application/octet-stream';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_name || `${doc.file_name}.${doc.file_type}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('documents.downloadError') || 'Dosya indirilemedi');
    }
  };

  const handleEdit = (doc) => {
    setEditName(doc.file_name);
    setEditCategory(doc.category);
    setEditDialog({ open: true, document: doc });
  };

  const handleSaveEdit = async () => {
    if (!editDialog.document) return;
    
    try {
      await api.put(`/documents/${editDialog.document.id}`, {
        file_name: editName,
        category: editCategory
      });
      
      toast.success(t('documents.updateSuccess') || 'Dosya güncellendi');
      setEditDialog({ open: false, document: null });
      fetchDocuments();
    } catch (error) {
      toast.error(t('documents.updateError') || 'Dosya güncellenemedi');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.document) return;
    
    try {
      await api.delete(`/documents/${deleteDialog.document.id}`);
      toast.success(t('documents.deleteSuccess') || 'Dosya silindi');
      setDeleteDialog({ open: false, document: null });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('documents.deleteError') || 'Dosya silinemedi');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocs.size === 0) return;
    
    try {
      // Use POST for bulk delete since DELETE with body is problematic
      await api.post('/documents/bulk-delete', Array.from(selectedDocs));
      toast.success(`${selectedDocs.size} dosya silindi`);
      setSelectedDocs(new Set());
      fetchDocuments();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(error.response?.data?.detail || t('documents.deleteError') || 'Dosyalar silinemedi');
    }
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = !searchTerm ||
      turkishIncludes(doc.file_name, searchTerm) ||
      turkishIncludes(doc.original_name, searchTerm);
    
    const matchesTab = activeTab === 'all' || doc.category === activeTab.toUpperCase();
    
    const matchesFirm = selectedFirmFilter === 'all' || 
      doc.firm_id === selectedFirmFilter ||
      (selectedFirmFilter === 'global' && !doc.firm_id);
    
    return matchesSearch && matchesTab && matchesFirm;
  });

  // Pagination calculation
  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDocuments = filteredDocuments.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, selectedFirmFilter]);

  // Filter users for assignment (first by firm, then by search with Turkish char support)
  const filteredUsers = usersForUpload.filter(u => 
    !userSearchTerm || 
    turkishIncludes(u.full_name, userSearchTerm) ||
    turkishIncludes(u.email, userSearchTerm)
  );

  // Count by category
  const companyDocs = documents.filter(d => d.category === 'COMPANY').length;
  const personalDocs = documents.filter(d => d.category === 'PERSONAL').length;

  return (
    <div className="space-y-6" data-testid="document-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('documents.management') || 'Doküman Yönetimi'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('documents.managementDesc') || 'Şirket belgelerini ve çalışan evraklarını yönetin'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedDocs.size > 0 && (
            <Button 
              variant="destructive" 
              size="sm"
              onClick={handleBulkDelete}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {selectedDocs.size} Sil
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4 mr-2" />
            {t('documents.upload') || 'Dosya Yükle'}
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t('documents.searchPlaceholder') || 'Dosya ara...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Firm Filter */}
        {canFilterFirm && firms.length > 0 && (
          <Select value={selectedFirmFilter} onValueChange={setSelectedFirmFilter}>
            <SelectTrigger className="w-[200px]">
              <Building2 className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder={t('documents.filterByFirm') || 'Firma Filtrele'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all') || 'Tümü'}</SelectItem>
              <SelectItem value="global">{t('documents.allCompanies') || 'Global (Tüm Firmalar)'}</SelectItem>
              {firms.map(firm => (
                <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            {t('documents.all') || 'Tümü'}
            <Badge variant="secondary" className="ml-1">{documents.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="company" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {t('documents.companyDocs') || 'Şirket Belgeleri'}
            <Badge variant="secondary" className="ml-1">{companyDocs}</Badge>
          </TabsTrigger>
          <TabsTrigger value="personal" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            {t('documents.personalDocs') || 'Kişisel Evraklar'}
            <Badge variant="secondary" className="ml-1">{personalDocs}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <Card className="p-12 text-center">
              <Folder className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">
                {t('documents.noDocuments') || 'Henüz dosya yok'}
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                {t('documents.noDocumentsAdminDesc') || 'Çalışanlarınız için dosya yükleyin'}
              </p>
              <Button onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4 mr-2" />
                {t('documents.uploadFirst') || 'İlk Dosyayı Yükle'}
              </Button>
            </Card>
          ) : (
            <>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedDocs.size === filteredDocuments.length && filteredDocuments.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedDocs(new Set(filteredDocuments.map(d => d.id)));
                          } else {
                            setSelectedDocs(new Set());
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>{t('documents.fileName') || 'Dosya Adı'}</TableHead>
                    {canFilterFirm && (
                      <TableHead>{t('documents.firm') || 'Firma'}</TableHead>
                    )}
                    <TableHead>{t('documents.category') || 'Kategori'}</TableHead>
                    <TableHead>{t('documents.assignedTo') || 'Atanan'}</TableHead>
                    <TableHead>{t('documents.size') || 'Boyut'}</TableHead>
                    <TableHead>{t('documents.uploadedAt') || 'Yüklenme'}</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedDocs.has(doc.id)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(selectedDocs);
                            if (checked) {
                              newSet.add(doc.id);
                            } else {
                              newSet.delete(doc.id);
                            }
                            setSelectedDocs(newSet);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {getFileIcon(doc.file_type)}
                          <div>
                            <p className="font-medium text-slate-900 dark:text-slate-100">{doc.file_name}</p>
                            <p className="text-xs text-slate-400">{doc.original_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      {canFilterFirm && (
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={!doc.firm_id ? "border-amber-200 text-amber-700 bg-amber-50" : "border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200"}
                          >
                            <Building2 className="h-3 w-3 mr-1" />
                            {doc.firm_name || 'Tüm Şirketler'}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={cn(
                            doc.category === 'COMPANY' 
                              ? "border-blue-200 text-blue-700 bg-blue-50" 
                              : "border-purple-200 text-purple-700 bg-purple-50"
                          )}
                        >
                          {doc.category === 'COMPANY' ? 'Şirket' : 'Kişisel'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {doc.access_level === 'super_admins' ? (
                          <Badge variant="outline" className="border-red-200 text-red-600 bg-red-50/50 text-xs">
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            Super Admin
                          </Badge>
                        ) : doc.access_level === 'firm_admins' ? (
                          <Badge variant="outline" className="border-amber-200 text-amber-600 bg-amber-50/50 text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            Firma Admin
                          </Badge>
                        ) : doc.assigned_to_all ? (
                          <Badge variant="secondary" className="text-xs">
                            <Users className="h-3 w-3 mr-1" />
                            Herkes
                          </Badge>
                        ) : (
                          (() => {
                            const assignees = getAssignees(doc);
                            const count = assignees.length;
                            const badge = (
                              <Badge variant="outline" className="text-xs cursor-help" data-testid={`assignees-badge-${doc.id}`}>
                                <User className="h-3 w-3 mr-1" />
                                {count} Kişi
                              </Badge>
                            );
                            if (count === 0) return badge;
                            return (
                              <HoverCard openDelay={150} closeDelay={100}>
                                <HoverCardTrigger asChild>{badge}</HoverCardTrigger>
                                <HoverCardContent side="top" align="start" className="w-64 p-3">
                                  <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
                                    Atananlar ({count})
                                  </p>
                                  <div className="space-y-0.5 max-h-64 overflow-y-auto">
                                    {assignees.slice(0, 20).map((a) => (
                                      <button
                                        key={a.id}
                                        type="button"
                                        onClick={() => navigate(`/dashboard/employees/${a.id}`)}
                                        className="w-full text-left text-sm px-2 py-1 rounded-md hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/30 dark:hover:text-blue-300 transition-colors truncate"
                                        data-testid={`assignee-link-${doc.id}-${a.id}`}
                                        title={`${a.name} — Profile git`}
                                      >
                                        {a.name}
                                      </button>
                                    ))}
                                    {count > 20 && (
                                      <p className="text-xs text-slate-400 pt-1 px-2">+{count - 20} daha…</p>
                                    )}
                                  </div>
                                </HoverCardContent>
                              </HoverCard>
                            );
                          })()
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 dark:text-slate-400 text-sm">
                        {formatFileSize(doc.file_size)}
                      </TableCell>
                      <TableCell className="text-slate-500 dark:text-slate-400 text-sm">
                        {formatDate(doc.created_at)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handlePreview(doc)}>
                              <Eye className="h-4 w-4 mr-2" />
                              {t('documents.preview') || 'Ön İzle'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownload(doc)}>
                              <Download className="h-4 w-4 mr-2" />
                              {t('documents.download') || 'İndir'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(doc)}>
                              <Edit className="h-4 w-4 mr-2" />
                              {t('common.edit') || 'Düzenle'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteDialog({ open: true, document: doc })}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t('common.delete') || 'Sil'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            {/* Pagination */}
            {filteredDocuments.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t mt-4">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span>{t('documents.showing') || 'Gösterilen'}:</span>
                  <span className="font-medium">{startIndex + 1}-{Math.min(endIndex, filteredDocuments.length)}</span>
                  <span>{t('documents.of') || '/'}</span>
                  <span className="font-medium">{filteredDocuments.length}</span>
                  <span>{t('documents.items') || 'dosya'}</span>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Items per page */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">{t('documents.perPage') || 'Sayfa başına'}:</span>
                    <Select value={String(itemsPerPage)} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Page navigation */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="h-8 px-2"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <ChevronLeft className="h-4 w-4 -ml-2" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8 px-2"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    <span className="px-3 text-sm">
                      {currentPage} / {totalPages || 1}
                    </span>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="h-8 px-2"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage >= totalPages}
                      className="h-8 px-2"
                    >
                      <ChevronRight className="h-4 w-4" />
                      <ChevronRight className="h-4 w-4 -ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              {t('documents.uploadFiles') || 'Dosya Yükle'}
            </DialogTitle>
            <DialogDescription>
              {t('documents.uploadDesc') || 'Dosyaları düzenleyin ve çalışanlara atayın'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Files List */}
            <div className="space-y-2">
              <Label>{t('documents.files') || 'Dosyalar'} ({uploadFiles.length})</Label>
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {uploadFiles.map((item, idx) => (
                  <div key={idx} className="p-3 flex items-center gap-3">
                    {getFileIcon(item.file.name.split('.').pop())}
                    <div className="flex-1 min-w-0">
                      <Input
                        placeholder={item.originalName}
                        value={item.customName}
                        onChange={(e) => {
                          const newFiles = [...uploadFiles];
                          newFiles[idx].customName = e.target.value;
                          setUploadFiles(newFiles);
                        }}
                        className="h-8 text-sm"
                      />
                      <p className="text-xs text-slate-400 mt-1">{item.originalName}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-red-500"
                      onClick={() => {
                        setUploadFiles(uploadFiles.filter((_, i) => i !== idx));
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Firm Selection - Super Admin Only */}
            {canFilterFirm && (
              <div className="space-y-2">
                <Label>{t('documents.targetFirm') || 'Hedef Firma'}</Label>
                <Select 
                  value={selectedFirmForUpload} 
                  onValueChange={(value) => {
                    setSelectedFirmForUpload(value);
                    setSelectedUsers([]); // Reset selected users when firm changes
                    setAssignToAll(true);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Firma Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-500" />
                        {t('documents.allCompanies') || 'Tüm Şirketler (Global)'}
                      </div>
                    </SelectItem>
                    {firms.map(firm => (
                      <SelectItem key={firm.id} value={firm.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {firm.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedFirmForUpload === 'all' && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    {t('documents.globalDocNote') || 'Global doküman tüm şirketlerin çalışanlarına görünür olacak'}
                  </p>
                )}
              </div>
            )}

            {/* Category */}
            <div className="space-y-2">
              <Label>{t('documents.category') || 'Kategori'}</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMPANY">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {t('documents.companyDocs') || 'Şirket Belgeleri'}
                    </div>
                  </SelectItem>
                  <SelectItem value="PERSONAL">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {t('documents.personalDocs') || 'Kişisel Evraklar'}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Assignment */}
            <div className="space-y-2">
              <Label>{t('documents.assignTo') || 'Erişim & Atama'}</Label>
              
              {/* Access Level Radio Options */}
              <div className="space-y-1.5 border rounded-lg p-3 bg-slate-50/50 dark:bg-slate-800/50">
                <label className="flex items-center gap-2.5 cursor-pointer p-1.5 rounded hover:bg-white dark:hover:bg-slate-700 transition-colors">
                  <input type="radio" name="accessLevel" value="myself" checked={accessLevel === 'myself'} onChange={() => { setAccessLevel('myself'); setAssignToAll(false); }} className="accent-teal-500" />
                  <User className="h-4 w-4 text-teal-500 shrink-0" />
                  <div>
                    <p className="text-sm font-medium">Kendime</p>
                    <p className="text-[11px] text-slate-400">Sadece kendi arşivimde görünsün</p>
                  </div>
                </label>

                <label className="flex items-center gap-2.5 cursor-pointer p-1.5 rounded hover:bg-white dark:hover:bg-slate-700 transition-colors">
                  <input type="radio" name="accessLevel" value="all" checked={accessLevel === 'all'} onChange={() => { setAccessLevel('all'); setAssignToAll(true); }} className="accent-blue-500" />
                  <Users className="h-4 w-4 text-blue-500 shrink-0" />
                  <div>
                    <p className="text-sm font-medium">Tüm Çalışanlar</p>
                    <p className="text-[11px] text-slate-400">Herkes bu belgeyi görebilir</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-2.5 cursor-pointer p-1.5 rounded hover:bg-white dark:hover:bg-slate-700 transition-colors">
                  <input type="radio" name="accessLevel" value="super_admins" checked={accessLevel === 'super_admins'} onChange={() => { setAccessLevel('super_admins'); setAssignToAll(true); }} className="accent-red-500" />
                  <ShieldCheck className="h-4 w-4 text-red-500 shrink-0" />
                  <div>
                    <p className="text-sm font-medium">Sadece Super Adminler</p>
                    <p className="text-[11px] text-slate-400">Yalnızca SUPER_ADMIN rolündeki kullanıcılar</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-2.5 cursor-pointer p-1.5 rounded hover:bg-white dark:hover:bg-slate-700 transition-colors">
                  <input type="radio" name="accessLevel" value="firm_admins" checked={accessLevel === 'firm_admins'} onChange={() => { setAccessLevel('firm_admins'); setAssignToAll(true); }} className="accent-amber-500" />
                  <Shield className="h-4 w-4 text-amber-500 shrink-0" />
                  <div>
                    <p className="text-sm font-medium">Firma Adminleri</p>
                    <p className="text-[11px] text-slate-400">Firma adminleri + Super Adminler görebilir</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-2.5 cursor-pointer p-1.5 rounded hover:bg-white dark:hover:bg-slate-700 transition-colors">
                  <input type="radio" name="accessLevel" value="custom" checked={accessLevel === 'custom'} onChange={() => { setAccessLevel('custom'); setAssignToAll(false); }} className="accent-violet-500" />
                  <User className="h-4 w-4 text-violet-500 shrink-0" />
                  <div>
                    <p className="text-sm font-medium">Kişi Seç</p>
                    <p className="text-[11px] text-slate-400">Belirli kullanıcıları seçin</p>
                  </div>
                </label>
              </div>

              {accessLevel === 'custom' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder={t('documents.searchUsers') || 'Çalışan ara...'}
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {filteredUsers.map(u => (
                      <div
                        key={u.id}
                        className={cn(
                          "p-2 flex items-center gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700",
                          selectedUsers.includes(u.id) && "bg-blue-50 dark:bg-blue-900/20"
                        )}
                        onClick={() => {
                          if (selectedUsers.includes(u.id)) {
                            setSelectedUsers(selectedUsers.filter(id => id !== u.id));
                          } else {
                            setSelectedUsers([...selectedUsers, u.id]);
                          }
                        }}
                      >
                        <Checkbox checked={selectedUsers.includes(u.id)} />
                        <div>
                          <p className="text-sm font-medium">{u.full_name}</p>
                          <p className="text-xs text-slate-400">{u.email} — <span className="font-medium">{u.role}</span></p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedUsers.length > 0 && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {selectedUsers.length} kişi seçildi
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button onClick={handleUpload} disabled={uploading || uploadFiles.length === 0}>
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {t('documents.upload') || 'Yükle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, document: editDialog.document })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('documents.editDocument') || 'Dosyayı Düzenle'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('documents.fileName') || 'Dosya Adı'}</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('documents.category') || 'Kategori'}</Label>
              <Select value={editCategory} onValueChange={setEditCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMPANY">Şirket Belgeleri</SelectItem>
                  <SelectItem value="PERSONAL">Kişisel Evraklar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, document: null })}>
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button onClick={handleSaveEdit}>
              {t('common.save') || 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, document: deleteDialog.document })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {t('documents.deleteConfirm') || 'Dosyayı Sil'}
            </DialogTitle>
            <DialogDescription>
              &quot;{deleteDialog.document?.file_name}&quot; dosyasını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, document: null })}>
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              {t('common.delete') || 'Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog for Images and PDFs */}
      <Dialog open={previewDialog.open} onOpenChange={(open) => !open && handleClosePreview()}>
        <DialogContent className="max-w-5xl max-h-[95vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              {previewDialog.document?.file_name}
              {isPdfFile(previewDialog.document?.file_type) && pdfNumPages && (
                <Badge variant="secondary" className="ml-2">
                  {pdfPageNumber} / {pdfNumPages}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-700 rounded-lg min-h-[500px] relative">
            {previewLoading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                <span className="ml-2 text-slate-600 dark:text-slate-300">{t('common.loading') || 'Yükleniyor...'}</span>
              </div>
            ) : previewDialog.blobUrl ? (
              <>
                {isImageFile(previewDialog.document?.file_type) && (
                  <div className="flex items-center justify-center p-4 min-h-[500px]">
                    <img
                      src={previewDialog.blobUrl}
                      alt={previewDialog.document?.file_name}
                      className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
                    />
                  </div>
                )}
                
                {isPdfFile(previewDialog.document?.file_type) && (
                  previewDialog.isDirectUrl ? (
                    /* Şub 2026 — S3 presigned URL: native browser PDF viewer (iframe).
                       react-pdf'in fetch() CORS ihtiyacını bypass eder ve dev/direkt açılır. */
                    <div className="w-full h-[75vh] p-2" data-testid="doc-pdf-iframe">
                      <iframe
                        src={previewDialog.blobUrl}
                        title={previewDialog.document?.file_name || 'PDF'}
                        className="w-full h-full rounded shadow border-0 bg-white"
                      />
                    </div>
                  ) : (
                  <div className="flex flex-col items-center p-4">
                    {/* PDF Controls */}
                    <div className="sticky top-0 z-10 flex items-center gap-2 mb-4 p-2 bg-white dark:bg-slate-800 rounded-lg shadow">
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={pdfPageNumber <= 1}
                        onClick={() => setPdfPageNumber(p => Math.max(1, p - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-slate-600 dark:text-slate-300 min-w-[80px] text-center">
                        {pdfPageNumber} / {pdfNumPages || '?'}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={!pdfNumPages || pdfPageNumber >= pdfNumPages}
                        onClick={() => setPdfPageNumber(p => Math.min(pdfNumPages || p, p + 1))}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <div className="w-px h-6 bg-slate-200 mx-2" />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setPdfScale(s => Math.max(0.5, s - 0.25))}
                      >
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-slate-600 dark:text-slate-300 min-w-[50px] text-center">
                        {Math.round(pdfScale * 100)}%
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setPdfScale(s => Math.min(3, s + 0.25))}
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* PDF Document */}
                    <Document
                      file={previewDialog.blobUrl}
                      onLoadSuccess={onPdfLoadSuccess}
                      loading={
                        <div className="flex items-center justify-center p-8">
                          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                        </div>
                      }
                      error={
                        <div className="text-center p-8 space-y-2">
                          <FileText className="h-12 w-12 text-red-400 mx-auto" />
                          <p className="text-red-500 font-medium">
                            {t('documents.pdfLoadError') || 'PDF yüklenemedi'}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {t('documents.pdfCorruptOrInvalid') || 'Dosya bozuk veya geçersiz bir PDF olabilir'}
                          </p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleDownload(previewDialog.document)}
                            className="mt-2"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            {t('documents.downloadInstead') || 'Dosyayı İndir'}
                          </Button>
                        </div>
                      }
                    >
                      <Page 
                        pageNumber={pdfPageNumber} 
                        scale={pdfScale}
                        className="shadow-lg"
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                      />
                    </Document>
                  </div>
                  )
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                {t('documents.noPreview') || 'Önizleme mevcut değil'}
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={handleClosePreview}>
              {t('common.close') || 'Kapat'}
            </Button>
            <Button onClick={() => handleDownload(previewDialog.document)}>
              <Download className="h-4 w-4 mr-2" />
              {t('documents.download') || 'İndir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentManagement;
